/**
 * The Supabase session.
 *
 * Storage decision (docs/16 section 3.3): the access token lives in memory and
 * only the refresh token is persisted. A script that reads storage gets a token
 * that is useless without the exchange endpoint, and the short-lived access
 * token is never at rest. The cost is one refresh round trip on page load.
 *
 * There is no dev-token path. The old frontend accepted a user UUID as a bearer
 * and honoured a `?dev_token=` query parameter; both are deliberately absent.
 */

const REFRESH_KEY = 'dealpulse.refresh'

let accessToken: string | null = null
let expiresAt = 0

export function getAccessToken(): string | null {
  return accessToken
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch (error) {
    // Storage can be unavailable (private mode, blocked cookies). That is a
    // degraded session, not a crash: the user signs in again this visit.
    console.warn('Session storage unavailable; sessions will not survive reload.', error)
    return null
  }
}

export function setSession(access: string, refresh: string | null, expiresInSeconds: number): void {
  accessToken = access
  expiresAt = Date.now() + Math.max(0, expiresInSeconds) * 1000
  if (refresh) {
    try {
      localStorage.setItem(REFRESH_KEY, refresh)
    } catch (error) {
      console.warn('Could not persist the refresh token; this session ends on reload.', error)
    }
  }
}

export function clearSession(): void {
  accessToken = null
  expiresAt = 0
  try {
    localStorage.removeItem(REFRESH_KEY)
  } catch (error) {
    console.warn('Could not clear stored session.', error)
  }
}

/** True when the access token is missing or within 60s of expiry. */
export function accessTokenExpired(): boolean {
  return !accessToken || Date.now() >= expiresAt - 60_000
}
